import { classifyChange, type ChangeClassification } from '../decisions/change-policy.js';
import { clamp } from '../units/round.js';
import type { PerformanceTrend } from './volume-response.js';

/**
 * Necesidad de deload (docs/TRAINING_ENGINE.md §5).
 *
 * Sistema hibrido: el deload planificado lo decide el calendario del bloque
 * (`training_weeks.intent = 'deload'`); esto es la mitad REACTIVA, que suma
 * señales de fatiga y propone actuar antes de que toque.
 *
 * Puro: recibe señales ya calculadas y devuelve una valoracion. Quien llama
 * decide de donde salen los datos y si la propuesta se aplica.
 */

// ---------------------------------------------------------------------------
// Pesos y umbrales
// ---------------------------------------------------------------------------

/**
 * Puntos de cada señal (§5.2). Configurables, como pide el documento.
 *
 * OJO: suman 110, no 100. El documento dice que `deload_score` va de 0 a 100 y
 * a la vez lista pesos que se pasan. Se resuelve recortando a 100 en lugar de
 * reescalar: reescalar moveria los umbrales de decision (35 y 55), que son la
 * parte del documento que de verdad decide algo. Con el recorte, alcanzar 100
 * solo significa "todas las señales menos una", que es igual de accionable.
 */
export const DELOAD_WEIGHTS = {
  e1rmDecline: 25,
  rirBelowTarget: 20,
  readinessLow: 20,
  persistentSoreness: 15,
  sleepLow: 10,
  missedSessions: 10,
  bodyweightDrop: 10,
} as const;

export const DELOAD_THRESHOLDS = {
  /** Patrones principales con descenso de e1RM >= 2,5 % en 2 semanas. */
  e1rmDeclinePatterns: 2,
  /** Proporcion de series con RIR real por debajo del programado. */
  rirBelowTargetRatio: 0.4,
  /**
   * Readiness medio de 7 dias.
   *
   * El documento lo escribe como "<= 2,5/5" pero `computeReadiness` devuelve
   * 0-100. 2,5 sobre 5 es la mitad de la escala, asi que aqui es 50. Sin esta
   * traduccion el umbral no se alcanzaria nunca y la señal estaria muerta.
   */
  readinessAvg: 50,
  readinessLowDays: 5,
  sleepHours: 6,
  sleepLowDays: 5,
  /** Check-ins consecutivos con agujetas >= 4/5 en la misma region. */
  sorenessStreak: 3,
  missedSessions: 2,
  /** Caida de peso en la semana, en tanto por uno. */
  bodyweightDropPct: 0.015,
  /** Cortes de decision. */
  partial: 35,
  full: 55,
  /** Por debajo de esto se puede posponer un deload planificado. */
  postpone: 20,
} as const;

/** Efecto de cada tipo de deload (§5.2). */
export const DELOAD_PRESCRIPTION = {
  partial: { setsFactor: 0.8, rirDelta: 1, intensityFactor: 1 },
  /** El documento da un rango: -40 a -50 % de volumen, intensidad -0 a -10 %. */
  full: { setsFactor: 0.55, rirDelta: 1, intensityFactor: 0.9 },
} as const;

// ---------------------------------------------------------------------------
// Entrada
// ---------------------------------------------------------------------------

export interface DeloadSignalsInput {
  /** Patrones principales con e1RM cayendo >= 2,5 % durante 2 semanas. */
  e1rmDecliningPatterns: number;
  /** Proporcion (0-1) de series con RIR real por debajo del programado. */
  rirBelowTargetRatio: number;
  /** Media movil de readiness de 7 dias, escala 0-100. */
  readiness7dAvg: number;
  /** Dias seguidos por debajo del umbral de readiness. */
  readinessLowDays: number;
  /** Media de horas de sueño. */
  sleepAvgHours: number;
  /** Dias seguidos por debajo de 6 h. */
  sleepLowDays: number;
  /** Check-ins seguidos con agujetas >= 4/5 en la MISMA region. */
  sorenessStreakCheckins: number;
  missedSessions: number;
  /** Caida de peso de la semana, en tanto por uno. */
  bodyweightDropPct: number;
  /**
   * Caida de peso que SI estaba programada, en tanto por uno. Sin esto, un
   * deficit deliberado se leeria como señal de fatiga y el sistema propondria
   * deload por hacer justo lo previsto.
   */
  plannedBodyweightDropPct: number;
  /**
   * Codigos de bandera roja activos (docs/SECURITY_PRIVACY.md §6.1). Si hay
   * alguna, no se propone nada: se detiene.
   */
  redFlags?: readonly string[];
}

// ---------------------------------------------------------------------------
// Valoracion
// ---------------------------------------------------------------------------

export type DeloadAction = 'none' | 'partial' | 'full' | 'halted';

export interface DeloadSignal {
  code: string;
  points: number;
}

export interface DeloadAssessment {
  /** 0-100. Informativo incluso cuando la accion es `halted`. */
  score: number;
  action: DeloadAction;
  /** Solo las señales que suman, para poder explicar la puntuacion. */
  signals: DeloadSignal[];
  /** Clasificacion del cambio. `null` cuando no hay nada que proponer. */
  change: ChangeClassification | null;
  redFlags: readonly string[];
}

export function deloadSignals(input: DeloadSignalsInput): DeloadSignal[] {
  const señales: DeloadSignal[] = [];
  const t = DELOAD_THRESHOLDS;

  if (input.e1rmDecliningPatterns >= t.e1rmDeclinePatterns) {
    señales.push({ code: 'e1rm_declining', points: DELOAD_WEIGHTS.e1rmDecline });
  }
  if (input.rirBelowTargetRatio >= t.rirBelowTargetRatio) {
    // Se llega al fallo antes de lo programado: la carga pesa mas de lo que deberia.
    señales.push({ code: 'rir_below_target', points: DELOAD_WEIGHTS.rirBelowTarget });
  }
  if (input.readiness7dAvg <= t.readinessAvg && input.readinessLowDays >= t.readinessLowDays) {
    señales.push({ code: 'readiness_low', points: DELOAD_WEIGHTS.readinessLow });
  }
  if (input.sorenessStreakCheckins >= t.sorenessStreak) {
    señales.push({ code: 'persistent_soreness', points: DELOAD_WEIGHTS.persistentSoreness });
  }
  if (input.sleepAvgHours < t.sleepHours && input.sleepLowDays >= t.sleepLowDays) {
    señales.push({ code: 'sleep_low', points: DELOAD_WEIGHTS.sleepLow });
  }
  if (input.missedSessions >= t.missedSessions) {
    señales.push({ code: 'missed_sessions', points: DELOAD_WEIGHTS.missedSessions });
  }
  // Solo cuenta la caida NO programada.
  if (input.bodyweightDropPct - input.plannedBodyweightDropPct > t.bodyweightDropPct) {
    señales.push({ code: 'bodyweight_drop', points: DELOAD_WEIGHTS.bodyweightDrop });
  }

  return señales;
}

/**
 * Valora la necesidad de deload y dice que hacer.
 *
 * Una bandera roja no se suma a la puntuacion ni la modifica: la sustituye
 * como criterio. El documento es explicito —«no se propone deload: se detienen
 * las recomendaciones»— y tiene sentido, porque proponer un deload ante un
 * sintoma clinico es responder a un problema medico con una decision de
 * entrenamiento. La puntuacion se sigue devolviendo, pero informa; no propone.
 */
export function assessDeload(input: DeloadSignalsInput): DeloadAssessment {
  const signals = deloadSignals(input);
  const bruto = signals.reduce((suma, señal) => suma + señal.points, 0);
  const score = clamp(bruto, 0, 100);
  const redFlags = input.redFlags ?? [];

  if (redFlags.length > 0) {
    return { score, action: 'halted', signals, change: null, redFlags };
  }

  if (score >= DELOAD_THRESHOLDS.full) {
    // Deload completo: siempre cambio relevante, pide aprobacion.
    return {
      score,
      action: 'full',
      signals,
      change: classifyChange({ kind: 'unplanned_deload' }),
      redFlags,
    };
  }

  if (score >= DELOAD_THRESHOLDS.partial) {
    // Reduccion parcial: es exactamente un cambio de volumen semanal del 20 %,
    // que la politica central clasifica como ajuste menor. Se reutiliza esa
    // clasificacion en lugar de declararlo menor por nuestra cuenta: el umbral
    // vive en un unico sitio.
    return {
      score,
      action: 'partial',
      signals,
      change: classifyChange({
        kind: 'weekly_volume',
        magnitude: 1 - DELOAD_PRESCRIPTION.partial.setsFactor,
      }),
      redFlags,
    };
  }

  return { score, action: 'none', signals, change: null, redFlags };
}

/** Series y RIR resultantes de aplicar la accion. `none` y `halted` no tocan nada. */
export function prescribeDeload(
  action: DeloadAction,
  currentSets: number,
  currentRir: number,
): { sets: number; rir: number; intensityFactor: number } {
  if (action === 'partial' || action === 'full') {
    const receta = DELOAD_PRESCRIPTION[action];
    return {
      sets: Math.round(currentSets * receta.setsFactor),
      rir: currentRir + receta.rirDelta,
      intensityFactor: receta.intensityFactor,
    };
  }
  return { sets: currentSets, rir: currentRir, intensityFactor: 1 };
}

// ---------------------------------------------------------------------------
// Posponer un deload planificado
// ---------------------------------------------------------------------------

export interface PostponeInput {
  score: number;
  performance: PerformanceTrend;
  /** Si ya se pospuso una vez en este bloque. Maximo uno (§5.2). */
  alreadyPostponedInBlock: boolean;
}

export interface PostponeDecision {
  postpone: boolean;
  reasonCodes: string[];
  change: ChangeClassification | null;
}

/**
 * Un deload planificado puede retrasarse una semana si no hace ninguna falta y
 * el rendimiento sigue subiendo. Como maximo una vez por bloque: sin ese tope,
 * la regla se aplicaria cada semana mientras las cosas fueran bien y el deload
 * no llegaria nunca, que es justo lo que el deload planificado existe para
 * evitar.
 */
export function shouldPostponeDeload(input: PostponeInput): PostponeDecision {
  if (input.alreadyPostponedInBlock) {
    return { postpone: false, reasonCodes: ['already_postponed_in_block'], change: null };
  }
  if (input.score >= DELOAD_THRESHOLDS.postpone) {
    return { postpone: false, reasonCodes: ['fatigue_signals_present'], change: null };
  }
  if (input.performance !== 'rising') {
    return { postpone: false, reasonCodes: ['performance_not_rising'], change: null };
  }
  return {
    postpone: true,
    reasonCodes: ['low_fatigue_and_rising'],
    change: classifyChange({ kind: 'week_reorganization', magnitude: 0 }),
  };
}

/** Texto corto para la tarjeta de propuesta, junto a la regla que lo decide. */
export function explainDeload(assessment: DeloadAssessment): string {
  if (assessment.action === 'halted') {
    return (
      'Recomendaciones detenidas por un aviso de seguridad. ' +
      'No se propone ningún ajuste de entrenamiento hasta resolverlo.'
    );
  }
  const cabecera = `Necesidad de descarga: ${assessment.score}/100`;
  if (assessment.action === 'none') return `${cabecera} · sin cambios`;
  if (assessment.action === 'partial') {
    return `${cabecera} · semana aligerada: −20 % de series y +1 RIR`;
  }
  return `${cabecera} · descarga completa propuesta: necesita tu aprobación`;
}
